import debounce from 'debounce'
import * as Sentry from '@sentry/react'

import type {
  TelemetryConfiguration,
  TelemetryMetric,
} from '@sketch/modules-common'

// Custom error type for telemetry errors so we can filter what kind of errors are logged into Sentry
class TelemetryError extends Error {}

let config: TelemetryConfiguration | undefined = undefined

// Set telemetry configuration
export const setConfig = (newConfig: TelemetryConfiguration) => {
  config = { ...newConfig }
}

// Send metrics to telemetry backend service
const sendMetrics = async (metrics: TelemetryMetric[]) => {
  if (!config) {
    return
  }

  const data = {
    meta: {
      client: 'webapp' as const,
      version: window.COMMIT_HASH!,
    },
    data: {
      metrics,
    },
  }
  const headers: Record<string, string> = {
    'content-type': 'application/json',
  }
  if (config.authToken) {
    headers.authorization = `bearer ${config.authToken}`
  }

  try {
    const response = await fetch(config.apiHost, {
      method: 'POST',
      mode: 'cors',
      headers,
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const text = await response.text()
      throw new TelemetryError(`${response.status}: ${text}`)
    }
  } catch (error) {
    // Only log telemetry errors to avoid flooding Sentry with network errors
    if (error instanceof TelemetryError) {
      Sentry.captureException(error, { extra: { data } })
    }
  }
}

// Collect and send metrics. Sending is debounced by 1 second so
// `trackMetric` calls in quick succession are batched together
const buildTrackMetric = () => {
  const metrics: TelemetryMetric[] = []

  const send = debounce(() => {
    sendMetrics(metrics)
    metrics.length = 0
  }, 1000)

  const track = (metric: TelemetryMetric) => {
    metrics.push(metric)
    send()
  }

  return track
}

export const trackMetric = buildTrackMetric()
